'use client';
import React, { useState, useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import MobilePartnerLayout from '@/components/MobilePartnerLayout';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';

interface UserProfile {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  whatsapp: string | null;
  agent_code: string | null;
  role: string;
  avatar_url: string | null;
  is_active: boolean;
  created_at: string;
}

interface EditProfileForm {
  full_name: string;
  phone: string;
  whatsapp: string;
  avatar_url: string;
}

interface LevelConfig {
  label: string;
  color: string;
  bg: string;
  border: string;
  icon: string;
  minClosing: number;
}

function getPartnerLevelConfig(closingCount: number): LevelConfig {
  if (closingCount >= 10)
    return { label: 'Platinum Partner', color: 'text-purple-700', bg: 'bg-purple-50', border: 'border-purple-200', icon: '💎', minClosing: 10 };
  if (closingCount >= 5)
    return { label: 'Gold Partner', color: 'text-yellow-700', bg: 'bg-yellow-50', border: 'border-yellow-200', icon: '🥇', minClosing: 5 };
  if (closingCount >= 2)
    return { label: 'Silver Partner', color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200', icon: '🥈', minClosing: 2 };
  return { label: 'Bronze Partner', color: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200', icon: '🥉', minClosing: 0 };
}

function getNextLevel(closingCount: number): { label: string; remaining: number } | null {
  if (closingCount >= 10) return null;
  if (closingCount >= 5) return { label: 'Platinum', remaining: 10 - closingCount };
  if (closingCount >= 2) return { label: 'Gold', remaining: 5 - closingCount };
  return { label: 'Silver', remaining: 2 - closingCount };
}

function EditProfileModal({
  profile,
  onClose,
  onSaved,
}: {
  profile: UserProfile;
  onClose: () => void;
  onSaved: (updated: Partial<UserProfile>) => void;
}) {
  const { user } = useAuth();
  const supabase = createClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState<EditProfileForm>({
    full_name: profile.full_name || '',
    phone: profile.phone || '',
    whatsapp: profile.whatsapp || profile.phone || '',
    avatar_url: profile.avatar_url || '',
  });
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>(profile.avatar_url || '');

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target?.result as string);
    reader.readAsDataURL(file);

    setUploadingPhoto(true);
    setError(null);
    try {
      const ext = file.name.split('.').pop();
      const filePath = `avatars/${user.id}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });

      if (uploadError) {
        const base64 = await new Promise<string>((resolve) => {
          const r = new FileReader();
          r.onload = (ev) => resolve(ev.target?.result as string);
          r.readAsDataURL(file);
        });
        setForm((prev) => ({ ...prev, avatar_url: base64 }));
      } else {
        const { data } = supabase.storage.from('avatars').getPublicUrl(filePath);
        setForm((prev) => ({ ...prev, avatar_url: data.publicUrl }));
      }
    } catch {
      setError('Gagal mengunggah foto. Coba lagi.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;
    if (!form.full_name.trim()) {
      setError('Nama tidak boleh kosong.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updates: Record<string, string | null> = {
        full_name: form.full_name.trim(),
        phone: form.phone.trim() || null,
        whatsapp: form.whatsapp.trim() || null,
        avatar_url: form.avatar_url || null,
      };

      const { error: updateError } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', user.id);

      if (updateError) throw updateError;

      onSaved(updates as Partial<UserProfile>);
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Gagal menyimpan perubahan.');
    } finally {
      setSaving(false);
    }
  };

  const initials = form.full_name
    ? form.full_name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()
    : '??';

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-card rounded-t-3xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom duration-300">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-muted-foreground/30" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h2 className="text-base font-bold text-foreground">Edit Data Saya</h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full hover:bg-muted transition-colors"
          >
            <Icon name="XMarkIcon" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-5 space-y-4 max-h-[72vh] overflow-y-auto">
          {/* Photo */}
          <div className="flex flex-col items-center gap-2">
            <div className="relative">
              {photoPreview ? (
                <img
                  src={photoPreview}
                  alt="Foto Profil"
                  className="w-24 h-24 rounded-full object-cover border-4 border-primary/20 shadow-md"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center border-4 border-primary/20 shadow-md">
                  <span className="text-3xl font-bold text-primary">{initials}</span>
                </div>
              )}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-primary flex items-center justify-center shadow-lg disabled:opacity-60 border-2 border-white"
              >
                {uploadingPhoto ? (
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Icon name="CameraIcon" size={15} className="text-white" />
                )}
              </button>
            </div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingPhoto}
              className="text-xs text-primary font-semibold disabled:opacity-60 py-1"
            >
              {uploadingPhoto ? 'Mengunggah...' : 'Ganti Foto Profil'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handlePhotoChange}
            />
          </div>

          {/* Name */}
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
              Nama Lengkap <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.full_name}
              onChange={(e) => setForm((prev) => ({ ...prev, full_name: e.target.value }))}
              placeholder="Masukkan nama lengkap"
              className="w-full px-4 py-3 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
              Nomor Telepon
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2">
                <Icon name="PhoneIcon" size={16} className="text-muted-foreground" />
              </span>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                placeholder="Contoh: 08123456789"
                className="w-full pl-9 pr-4 py-3 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
            </div>
          </div>

          {/* WhatsApp */}
          <div>
            <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
              Nomor WhatsApp
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-green-500">
                <Icon name="ChatBubbleLeftRightIcon" size={16} />
              </span>
              <input
                type="tel"
                value={form.whatsapp}
                onChange={(e) => setForm((prev) => ({ ...prev, whatsapp: e.target.value }))}
                placeholder="Contoh: 08123456789"
                className="w-full pl-9 pr-4 py-3 rounded-xl border border-border bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Nomor ini digunakan untuk dihubungi oleh calon pembeli.
            </p>
          </div>

          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              <Icon name="ExclamationCircleIcon" size={16} className="text-red-500 flex-shrink-0" />
              <p className="text-xs text-red-600">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-border bg-card">
          <button
            onClick={handleSave}
            disabled={saving || uploadingPhoto}
            className="w-full py-3.5 bg-primary text-white rounded-xl font-bold text-sm active:scale-95 transition-all disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Menyimpan...
              </>
            ) : (
              <>
                <Icon name="CheckIcon" size={16} />
                Simpan Perubahan
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MobileProfilPage() {
  const { user, signOut } = useAuth();
  const router = useRouter();
  const supabase = createClient();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [signingOut, setSigningOut] = useState(false);
  const [closingCount, setClosingCount] = useState(0);
  const [leadCount, setLeadCount] = useState(0);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }
    const fetchData = async () => {
      setLoading(true);
      const [profileRes, closingRes, leadRes] = await Promise.all([
        supabase
          .from('user_profiles')
          .select('id, full_name, email, phone, whatsapp, agent_code, role, avatar_url, is_active, created_at')
          .eq('id', user.id)
          .single(),
        supabase
          .from('leads')
          .select('id', { count: 'exact', head: true })
          .eq('agent_id', user.id)
          .eq('stage', 'Closing'),
        supabase
          .from('leads')
          .select('id', { count: 'exact', head: true })
          .eq('agent_id', user.id),
      ]);
      if (profileRes.data) setProfile(profileRes.data as UserProfile);
      if (closingRes.count !== null) setClosingCount(closingRes.count);
      if (leadRes.count !== null) setLeadCount(leadRes.count);
      setLoading(false);
    };
    fetchData();
  }, [user]);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await signOut();
      router.push('/login');
    } catch {
      setSigningOut(false);
    }
  };

  const handleProfileSaved = (updated: Partial<UserProfile>) => {
    setProfile((prev) => (prev ? { ...prev, ...updated } : prev));
  };

  const initials = profile?.full_name
    ? profile.full_name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : '??';

  const levelConfig = getPartnerLevelConfig(closingCount);
  const nextLevel = getNextLevel(closingCount);
  const progressPct = nextLevel
    ? Math.min(100, Math.round(((closingCount - levelConfig.minClosing) / (closingCount + nextLevel.remaining - levelConfig.minClosing)) * 100))
    : 100;

  const menuItems = [
    {
      label: 'Data Saya',
      icon: 'UserIcon',
      description: 'Edit nama, foto, & kontak',
      action: () => setShowEditModal(true),
    },
    {
      label: 'Level & Reward',
      icon: 'TrophyIcon',
      description: 'Lihat poin & hadiah Anda',
      href: '/partner/mobile/reward',
    },
    {
      label: 'Link Referral',
      icon: 'LinkIcon',
      description: 'Bagikan link referral Anda',
      href: '/partner/mobile/home',
    },
    {
      label: 'Marketing Kit',
      icon: 'PhotoIcon',
      description: 'Materi promosi & brosur',
      href: '/partner/marketing-kit',
    },
    {
      label: 'Bantuan',
      icon: 'QuestionMarkCircleIcon',
      description: 'FAQ & hubungi support',
      href: '#',
    },
  ];

  return (
    <MobilePartnerLayout showFloatingButton={false}>
      {/* Hero Header */}
      <div className="relative bg-primary overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/5" />
        <div className="absolute -bottom-12 -left-6 w-32 h-32 rounded-full bg-white/5" />
        <div className="px-5 pt-12 pb-20">
          <div className="flex items-center justify-between">
            <h1 className="text-white font-bold text-xl">Profil Saya</h1>
            <button
              onClick={() => setShowEditModal(true)}
              className="p-2 rounded-full bg-white/20 text-white active:bg-white/30 transition-colors"
              aria-label="Edit Profil"
            >
              <Icon name="PencilSquareIcon" size={18} />
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 -mt-14 space-y-4 pb-28">
        {/* ── Profile Card ── */}
        <div className="bg-card rounded-2xl border border-border shadow-md overflow-hidden">
          {loading ? (
            <div className="p-5 animate-pulse">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-full bg-muted flex-shrink-0" />
                <div className="space-y-2 flex-1">
                  <div className="h-5 bg-muted rounded w-3/4" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                  <div className="h-3 bg-muted rounded w-1/3" />
                </div>
              </div>
            </div>
          ) : profile ? (
            <>
              {/* Avatar + Info */}
              <div className="p-5 flex items-start gap-4">
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                  {profile.avatar_url ? (
                    <img
                      src={profile.avatar_url}
                      alt={profile.full_name}
                      className="w-20 h-20 rounded-2xl object-cover border-2 border-primary/20 shadow-sm"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center border-2 border-primary/20 shadow-sm">
                      <span className="text-2xl font-bold text-primary">{initials}</span>
                    </div>
                  )}
                  <button
                    onClick={() => setShowEditModal(true)}
                    className="absolute -bottom-1.5 -right-1.5 w-7 h-7 rounded-full bg-primary flex items-center justify-center shadow-md border-2 border-white"
                    aria-label="Ganti foto"
                  >
                    <Icon name="CameraIcon" size={13} className="text-white" />
                  </button>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0 pt-1">
                  <h2 className="text-lg font-bold text-foreground leading-tight truncate">
                    {profile.full_name || '—'}
                  </h2>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">{profile.email}</p>

                  {/* Level badge */}
                  <div className={`inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full border text-xs font-semibold ${levelConfig.bg} ${levelConfig.border} ${levelConfig.color}`}>
                    <span>{levelConfig.icon}</span>
                    <span>{levelConfig.label}</span>
                  </div>

                  {/* Agent code */}
                  {profile.agent_code ? (
                    <div className="flex items-center gap-1.5 mt-2">
                      <div className="bg-primary/10 rounded-lg px-2.5 py-1 flex items-center gap-1.5">
                        <Icon name="IdentificationIcon" size={13} className="text-primary" />
                        <span className="text-xs font-bold text-primary tracking-wide">
                          {profile.agent_code}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 mt-2">
                      <div className="bg-muted rounded-lg px-2.5 py-1 flex items-center gap-1.5">
                        <Icon name="IdentificationIcon" size={13} className="text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Kode belum ditetapkan</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Contact info row */}
              <div className="border-t border-border px-5 py-3 grid grid-cols-2 divide-x divide-border">
                <div className="pr-4">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-0.5">
                    WhatsApp
                  </p>
                  <p className="text-xs font-semibold text-foreground truncate">
                    {profile.whatsapp || profile.phone || (
                      <span className="text-muted-foreground italic">Belum diisi</span>
                    )}
                  </p>
                </div>
                <div className="pl-4">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-0.5">
                    Status
                  </p>
                  <div className="flex items-center gap-1">
                    <span className={`w-1.5 h-1.5 rounded-full ${profile.is_active ? 'bg-green-500' : 'bg-red-400'}`} />
                    <p className={`text-xs font-semibold ${profile.is_active ? 'text-green-600' : 'text-red-500'}`}>
                      {profile.is_active ? 'Aktif' : 'Tidak Aktif'}
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="p-5 text-center text-sm text-muted-foreground">
              Gagal memuat profil.
            </div>
          )}
        </div>

        {/* ── Stats Card ── */}
        {!loading && (
          <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
            <div className="px-5 pt-4 pb-3">
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-3">
                Statistik Saya
              </p>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-primary/5 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-primary">{leadCount}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5 font-medium">Total Lead</p>
                </div>
                <div className="bg-green-50 rounded-xl p-3 text-center">
                  <p className="text-2xl font-bold text-green-700">{closingCount}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5 font-medium">Closing</p>
                </div>
                <div className={`rounded-xl p-3 text-center ${levelConfig.bg}`}>
                  <p className="text-xl font-bold">{levelConfig.icon}</p>
                  <p className={`text-[10px] font-semibold mt-0.5 ${levelConfig.color}`}>
                    {levelConfig.label.split(' ')[0]}
                  </p>
                </div>
              </div>
            </div>

            {/* Level progress */}
            {nextLevel && (
              <div className="px-5 pb-4">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-[10px] text-muted-foreground">
                    Menuju <span className="font-semibold text-foreground">{nextLevel.label}</span>
                  </p>
                  <p className="text-[10px] font-semibold text-primary">
                    {nextLevel.remaining} closing lagi
                  </p>
                </div>
                <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all duration-500"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              </div>
            )}
            {!nextLevel && (
              <div className="px-5 pb-4">
                <div className="flex items-center gap-2 bg-purple-50 rounded-xl px-3 py-2">
                  <span className="text-base">💎</span>
                  <p className="text-xs font-semibold text-purple-700">
                    Anda sudah mencapai level tertinggi!
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Menu Items ── */}
        <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
          {menuItems.map((item, idx) => {
            const isLast = idx === menuItems.length - 1;
            const inner = (
              <>
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Icon name={item.icon as any} size={20} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground">{item.label}</p>
                  {item.description && (
                    <p className="text-[11px] text-muted-foreground mt-0.5">{item.description}</p>
                  )}
                </div>
                <Icon name="ChevronRightIcon" size={16} className="text-muted-foreground flex-shrink-0" />
              </>
            );

            const baseClass = `flex items-center gap-3 px-4 py-3.5 active:bg-muted transition-colors ${
              !isLast ? 'border-b border-border' : ''
            }`;

            if (item.action) {
              return (
                <button
                  key={item.label}
                  onClick={item.action}
                  className={`w-full text-left ${baseClass}`}
                >
                  {inner}
                </button>
              );
            }
            return (
              <Link key={item.label} href={item.href!} className={baseClass}>
                {inner}
              </Link>
            );
          })}
        </div>

        {/* ── Logout Button ── */}
        <button
          onClick={handleSignOut}
          disabled={signingOut}
          className="w-full flex items-center justify-center gap-2.5 py-4 bg-red-50 border border-red-200 text-red-600 rounded-2xl font-bold text-sm active:scale-95 transition-all disabled:opacity-60"
        >
          {signingOut ? (
            <>
              <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
              Keluar...
            </>
          ) : (
            <>
              <Icon name="ArrowRightOnRectangleIcon" size={18} />
              Keluar dari Akun
            </>
          )}
        </button>

        {/* App version */}
        <p className="text-center text-[11px] text-muted-foreground pb-2">
          BETALAND Partner App v1.0
        </p>
      </div>

      {/* Edit Profile Modal */}
      {showEditModal && profile && (
        <EditProfileModal
          profile={profile}
          onClose={() => setShowEditModal(false)}
          onSaved={handleProfileSaved}
        />
      )}
    </MobilePartnerLayout>
  );
}
